// HomeBackground2.jsx
import React, { useContext, useEffect, useRef } from "react";
import "./HomeBackground.css";
import { gsap } from "gsap";

import * as THREE from "three";
import { createCamera } from "./components/camera.js";
import { createLights } from "./components/lights.js";
import { createScene } from "./components/scene.js";
import { createRenderer } from "./systems/renderer.js";
import {
    createAxisHelper,
    createControls,
    createGUI,
    createStats
} from "./systems/debug.js";
import { Resizer } from "./systems/Resizer";
import { renderSVG } from "./systems/renderSVG.js";
import {
    transformLogo,
    transformLogoAbout, transformLogoAboutDesktop, transformLogoAboutMobile, transformLogoAboutTablet,
    transformLogoContact,
    transformLogoContactDesktop,
    transformLogoContactMobile,
    transformLogoContactTablet,
    transformLogoDefault,
    transformLogoHomeDesktop,
    transformLogoHomeMobile,
    transformLogoHomeTablet, transformLogoLoginDesktop, transformLogoLoginMobile, transformLogoLoginMobileLandscape,
    transformLogoLoginTablet
} from "./systems/constants.js";
import { useSnapshot } from "valtio";
import state from "../store/index.js";
import { useLocation } from "react-router-dom";
import { getThemeContext } from "../providers/ThemeProvider.jsx";

let isDebug = false;

const HomeBackground = ({ }) => {
    const containerRef = useRef(null); // Referencia al contenedor del canvas
    const theme  = getThemeContext(); // Obtener el tema actual del contexto
    const location = useLocation(); // Obtener la ubicación actual de react-router
    // var themeContext = getThemeContext();

    // Referencias de Three.js
    const sceneRef = useRef(null);
    const cameraRef = useRef(null);
    const rendererRef = useRef(null);
    const statsRef = useRef(null);
    const svgGroupRef = useRef(null);
    const clockRef = useRef(new THREE.Clock());
    const lastElapsedTimeRef = useRef(0);
    const tamanoExtrusionRef = useRef(0.0001);
    const contadorTiempoRef = useRef(0);
    const observerRef = useRef(null);
    const observerRetryRef = useRef(0);
    const materialLightRef = useRef(null);
    const materialDarkRef = useRef(null);

    // Variables para manejar actualizaciones globales (si es necesario)
    window.update_aux_function = useRef(null);
    window.update_color_aux_function = useRef(null);


    // Cargar y renderizar el SVG
    const  urlSVGLight = "/LogoNegro.svg";
    const urlSVGDark = "/LogoBlanco.svg";
    const urlSVG = theme === "dark" ? urlSVGDark : urlSVGLight;
    const { object, update, updateColor, showOrHide, changeSize, setOnTheMiddle,fitCameraToCenteredObject } = renderSVG(tamanoExtrusionRef.current, urlSVG);

    useEffect(() => {
        // Inicializar Three.js
        // console.log("Initializing Three.js...");
        const scene = createScene();
        sceneRef.current = scene;

        // Crear y añadir luces
        const hemiLight = createLights();
        scene.add(hemiLight);

        // Crear la cámara
        const camera = createCamera();
        cameraRef.current = camera;

        // Crear el renderizador
        const renderer = createRenderer();
        rendererRef.current = renderer;

        // Añadir el canvas al contenedor
        const container = containerRef.current;
        container.appendChild(renderer.domElement);

        // Añadir helpers de debug si está activado
        if (isDebug) {
            createAxisHelper(isDebug, scene);
            createControls(isDebug, camera, renderer);
            statsRef.current = createStats(isDebug);
            createGUI(null);
        }

        // Renderizar la escena inicial
        renderer.render(scene, camera);

        svgGroupRef.current = object;
        scene.add(svgGroupRef.current);

        // Asignar funciones de actualización globales
        state.extrusion = tamanoExtrusionRef.current;

        // Manejar el redimensionamiento
        new Resizer(container, camera, renderer);

        // Posicionar la cámara
        camera.position.z = 400;

        // console.log("sceneeeee", scene);
        // changeSize(scene, window.innerWidth / 1000, false, true, scene.children[1]);
        // Iniciar el bucle de animación
        const animate = () => {

            requestAnimationFrame(animate);
            if (isDebug && statsRef.current) {
                statsRef.current.update();
            }

            const elapsedTime = clockRef.current.getElapsedTime();
            const deltaTime = elapsedTime - lastElapsedTimeRef.current;
            lastElapsedTimeRef.current = elapsedTime;

            renderer.render(scene, camera);
        };

        animate();

        // Configurar Intersection Observer



        // setOnTheMiddle(sceneRef.current);
        // changeSize(sceneRef.current, window.innerWidth / 1000, false, );
        // setOnTheMiddle(sceneRef.current);

        // Configurar el temporizador
        const interval = setInterval(() => {
            contadorTiempoRef.current++;
        }, 1000);


        // console.log("Three.js initialized.");
        // ── Control externo: About page scroll-driven animation ──────────
        let aboutExtTween = null;
        const aboutExtObj = { tamanoextrusion: tamanoExtrusionRef.current };

        const handleAboutScroll = (e) => {
            if (!svgGroupRef.current || !sceneRef.current) return;
            const { rotationX, rotationY, rotationZ, extrusion_depth, positionX, positionY, positionZ } = e.detail;
            if (extrusion_depth !== undefined) {
                aboutExtObj.tamanoextrusion = tamanoExtrusionRef.current;
                if (aboutExtTween) aboutExtTween.kill();
                aboutExtTween = gsap.to(aboutExtObj, {
                    duration: 0.12,
                    tamanoextrusion: extrusion_depth,
                    ease: "none",
                    onUpdate: () => {
                        tamanoExtrusionRef.current = aboutExtObj.tamanoextrusion;
                        update(aboutExtObj.tamanoextrusion, theme, rendererRef.current, sceneRef.current, cameraRef.current);
                    },
                });
            }
            const rotTarget = {};
            if (rotationX !== undefined) rotTarget.x = rotationX;
            if (rotationY !== undefined) rotTarget.y = rotationY;
            if (rotationZ !== undefined) rotTarget.z = rotationZ;
            if (Object.keys(rotTarget).length)
                gsap.to(svgGroupRef.current.rotation, { duration: 0.2, ease: "none", ...rotTarget });
            const posTarget = {};
            if (positionX !== undefined) posTarget.x = positionX;
            if (positionY !== undefined) posTarget.y = positionY;
            if (positionZ !== undefined) posTarget.z = positionZ;
            if (Object.keys(posTarget).length)
                gsap.to(sceneRef.current.position, { duration: 0.2, ease: "none", ...posTarget });
        };
        window.addEventListener('customiz3d:about-scroll', handleAboutScroll);

        // ── Contact page scroll-driven extrusion ─────────────────────────
        let contactExtTween = null;
        const contactExtObj = { tamanoextrusion: tamanoExtrusionRef.current };

        const handleContactScroll = (e) => {
            if (!svgGroupRef.current || !sceneRef.current) return;
            const { extrusion_depth, positionY } = e.detail;

            if (extrusion_depth !== undefined) {
                contactExtObj.tamanoextrusion = tamanoExtrusionRef.current;
                if (contactExtTween) contactExtTween.kill();
                contactExtTween = gsap.to(contactExtObj, {
                    duration: 0.12,
                    tamanoextrusion: extrusion_depth,
                    ease: "none",
                    onUpdate: () => {
                        tamanoExtrusionRef.current = contactExtObj.tamanoextrusion;
                        update(contactExtObj.tamanoextrusion, theme, rendererRef.current, sceneRef.current, cameraRef.current);
                    },
                });
            }
            if (positionY !== undefined) {
                gsap.to(sceneRef.current.position, { duration: 0.12, y: positionY, ease: "none" });
            }
        };
        window.addEventListener('customiz3d:contact-scroll', handleContactScroll);

        // Manejar redimensionamiento
        const handleResize = () => {
            changeDataByDevice();
            animateCosas();
        };
        window.addEventListener('resize', handleResize);

        // Limpieza al desmontar el componente
        return () => {
            window.removeEventListener('customiz3d:about-scroll', handleAboutScroll);
            window.removeEventListener('customiz3d:contact-scroll', handleContactScroll);
            window.removeEventListener('resize', handleResize);
            if (observerRef.current?.observer) {
                observerRef.current.observer.disconnect();
            }
            if (rendererRef.current) {
                rendererRef.current.dispose();
                container.removeChild(rendererRef.current.domElement);
            }
            clearInterval(interval);
        };
        // changeSize(sceneRef.current, window.innerWidth / 1000, false, true);


        //Eventlistener resizer

        // setOnTheMiddle(svgGroupRef.current);
        // setOnTheMiddleNew(svgGroupRef.current, cameraRef.current, rendererRef.current, sceneRef.current);


    }, []); // Ejecutar solo una vez al montar



    const changeDataByDevice = () => {
        const deviceType = getDeviceType();
        const currentPath = window.location.pathname;
        if (currentPath === "/about") {
            // data = transformLogoAbout["section-0"];
            data =
                deviceType === "mobile"
                    ? transformLogoAboutMobile["section-0"]
                    : deviceType === "tablet"
                        ? transformLogoAboutTablet["section-0"]
                        : transformLogoAboutDesktop["section-0"];
        } else if (currentPath === "/contact") {
            data =
                deviceType === "mobile"
                    ? transformLogoContactMobile["section-0"]
                    : deviceType === "tablet"
                        ? transformLogoContactTablet["section-0"]
                        : transformLogoContactDesktop["section-0"];
        } else if (["/login", "/register", "/verify-email"].includes(currentPath)) {
            // Páginas de auth: tarjeta a la derecha, logo visible a la izquierda
            switch (deviceType) {
                case "mobile":
                    data = transformLogoLoginMobile["section-0"];
                    break;
                case "mobile-landscape":
                    data = transformLogoLoginMobileLandscape["section-0"];
                    break;
                case "tablet":
                    data = transformLogoLoginTablet["section-0"];
                    break;
                case "desktop":
                default:
                    data = transformLogoLoginDesktop["section-0"];
                    break;
            }
        } else if (currentPath === "/") {
            data =
                deviceType === "mobile"
                    ? transformLogoHomeMobile["section-0"]
                    : deviceType === "tablet"
                        ? transformLogoHomeTablet["section-0"]
                        : transformLogoHomeDesktop["section-0"];

        } else {
            data = transformLogoDefault["section-0"];
        }
    }

    const animateCosas = () => {
        const tamanoextrusionObj = { tamanoextrusion: tamanoExtrusionRef.current };

        gsap.to(tamanoextrusionObj, {
            duration: data?.durationExtrusion ? data.durationExtrusion : 1.5,
            tamanoextrusion: data.extrusion_depth,
            onUpdate: () => {
                update(tamanoextrusionObj.tamanoextrusion, theme, rendererRef.current, sceneRef.current, cameraRef.current);
            },
        });

        // Animar rotación
        gsap.to(svgGroupRef?.current.rotation, {
            duration: 1.5,
            ease: "power2.inOut",
            x: data?.rotationX ?? 0,
            y: data?.rotationY ?? 0,
            z: data?.rotationZ ?? 0,
        });

        // Animar posición
        gsap.to(sceneRef?.current.position, {
            duration: 2,
            ease: "power2.inOut",
            x: data?.positionX ?? 0,
            y: data?.positionY ?? 0,
            z: data?.positionZ ?? 0,
        });
    }
    useEffect(() => {

        // Detectar cambios en el tema
        handleThemeChange(theme);
    }, [theme]);

    useEffect(() => {

        // Detectar cambios en el tema
        handleThemeChange(theme);
        const fractionSize = window.innerWidth / 1000; // Ajusta según tus necesidades
        //


        // changeDataByDevice();
        // animateCosas();
        // changeSize(sceneRef.current, fractionSize,false);
        // trigger resize event.
        // window.dispatchEvent(new Event('resize'));
    }, []);


    function resizeAndCenter(camera, object, container, padding = 1.2) {
        // Obtener los límites del objeto
        const box = new THREE.Box3().setFromObject(object);
        const center = new THREE.Vector3();
        box.getCenter(center);

        // Calcular el tamaño del objeto
        const size = new THREE.Vector3();
        box.getSize(size);

        const maxDim = Math.max(size.x, size.y, size.z);

        // Ajustar la cámara para que encaje el objeto
        const fov = camera.fov * (Math.PI / 180);
        let distance = maxDim / (2 * Math.tan(fov / 2));
        distance *= padding;

        // Ajustar la cámara y centrarla
        camera.position.set(center.x, center.y, center.z + distance);
        camera.lookAt(center);

        // Escalar el objeto si es necesario
        const containerWidth = container.clientWidth || window.innerWidth;
        const containerHeight = container.clientHeight || window.innerHeight;
        const aspectRatio = containerWidth / containerHeight;

        // Actualizar la proyección de la cámara
        camera.aspect = aspectRatio;
        camera.updateProjectionMatrix();

        // Actualizar el tamaño del renderizador
        rendererRef.current.setSize(containerWidth, containerHeight);
    }

    const handleThemeChange = (newTheme) => {


        if (newTheme === 'dark') {
            // update(tamanoExtrusionRef.current, theme, sceneRef.current);
            updateColor("#666968", theme, sceneRef.current);

            // Otras actualizaciones necesarias para el tema oscuro
        } else if (newTheme === 'light') {
                // updateColor("#00ff00");
            // rendererRef.render(scene, camera);
            updateColor("#eaeaea", theme, sceneRef.current);

            // rendererRef.current.render()

            // Otras actualizaciones necesarias para el tema claro
        }

        // Puedes agregar más lógica según las necesidades de tu escena
    };

    useEffect(() => {
        // Detectar cambios en la ubicación
        setupIntersectionObserver();
        changeDataByDevice();
        animateCosas();

        // Rutas en las que el logo 3D del fondo debe estar visible
        const logoVisiblePaths = ["/", "/about", "/contact", "/login", "/register", "/verify-email"];
        showOrHide(sceneRef.current, logoVisiblePaths.includes(location.pathname));

    }, [location]);
    const getDeviceType = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const isLandscape = width > height;

        // Definir un umbral de altura para distinguir entre mobile-landscape y tablet
        const mobileLandscapeHeightThreshold = 600; // Puedes ajustar este valor según tus necesidades

        if (isLandscape && height <= mobileLandscapeHeightThreshold) {
            return "mobile-landscape";
        }

        if (width <= 768) {
            return "mobile";
        }
        if (width <= 1024) {
            return "tablet";
        }
        return "desktop";
    };


    var data = [];

    const setupIntersectionObserver = () => {
        // Desconectar el observer anterior para evitar acumulación
        if (observerRef.current?.observer) {
            observerRef.current.observer.disconnect();
        }
        observerRef.current = { observer: null, currentSection: null };

        const tamanoextrusionObj = { tamanoextrusion: tamanoExtrusionRef.current };

        const scrollContainer = document.querySelector(".wrapper_background");
        const currentPath = window.location.pathname;

        // Si estamos en home pero el container aún no está en el DOM
        // (AnimatePresence está animando la entrada), reintentamos el próximo frame.
        if (currentPath === "/" && !scrollContainer) {
            if (observerRetryRef.current < 15) {
                observerRetryRef.current++;
                console.log(`[BG] retry ${observerRetryRef.current} — home container not ready yet`);
                requestAnimationFrame(() => setupIntersectionObserver());
            }
            return;
        }
        observerRetryRef.current = 0;

        // Solo crear observer si estamos realmente en home y el contenedor existe.
        // Aunque AnimatePresence mantenga el home montado durante transición de salida,
        // el pathname ya habrá cambiado — en ese caso no creamos el observer.
        if (!scrollContainer || currentPath !== "/") {
            console.log(`[BG] setupObserver skip — path="${currentPath}" container=${!!scrollContainer}`);

            /*console.warn(
                "No .wrapper_background container found. IntersectionObserver won't work."
            );*/

            // console.log("location", location.pathname);
            // console.log("theme", theme);

            /*if (location.pathname === "/about") {
                data = transformLogoAbout["section-0"];
                // console.log("SVGGG", svgGroupRef.current);
                // updateColor("green");
            } else if (location.pathname === "/contact") {
                data = transformLogoContact["section-0"];
            } else {
                data = transformLogoDefault["section-0"];
            }*/
            changeDataByDevice();

            // Animar la profundidad de extrusión
            gsap.to(tamanoextrusionObj, {
                duration: 1.5,
                tamanoextrusion: 1,
                onUpdate: () => {
                    update(tamanoextrusionObj.tamanoextrusion, theme, rendererRef.current, sceneRef.current, cameraRef.current);
                },
            });

            // Animar rotación
            gsap.to(svgGroupRef.current.rotation, {
                duration: 1.5,
                ease: "power2.inOut",
                x: data?.rotationX ?? 0,
                y: data?.rotationY ?? 0,
                z: data?.rotationZ ?? 0,
            });

            // Animar posición
            gsap.to(sceneRef.current.position, {
                duration: 2,
                ease: "power2.inOut",
                x: data?.positionX ?? 0,
                y: data?.positionY ?? 0,
                z: data?.positionZ ?? 0,
            });
            // rendererRef.render(sceneRef, cameraRef);

            return;
        }

        const options = {
            root: scrollContainer, // el contenedor desplazable
            rootMargin: "0px",
            threshold: 0.05 // se activa cuando el 5% de la sección es visible
        };

        console.log(`[BG] Observer CREADO — path="${window.location.pathname}" sections=${document.querySelectorAll('[id^="section-"]').length}`);

        const callback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const newSectionId = entry.target.id; // e.g. "section-0"

                    // Solo animar si es una nueva sección
                    if (newSectionId !== observerRef.current.currentSection && svgGroupRef.current) {
                        console.log(`[BG] Sección → ${newSectionId} | path="${window.location.pathname}"`);
                        observerRef.current.currentSection = newSectionId;

                        // const data = transformLogo[newSectionId];
                        var data;
                        var oldData ;
                        const deviceType = getDeviceType();

                        // var oldData;
                        // const deviceType = getDeviceType();

                        // Usar window.location.pathname para evitar closures stale
                        const currentPath = window.location.pathname;
                        if (currentPath === "/") {
                            data =
                                deviceType === "mobile"
                                    ? transformLogoHomeMobile[newSectionId]
                                    : deviceType === "tablet"
                                        ? transformLogoHomeTablet[newSectionId]
                                        : transformLogoHomeDesktop[newSectionId];

                        } else if (currentPath === "/login") {
                            // sin datos de sección para login
                        }
                        console.log("dataaaa", data);
                        if (!data) {
                            console.warn(
                                `No transform data found for section ID: ${newSectionId}`
                            );
                            return;
                        }


                        // Animar la profundidad de extrusión
                        gsap.to(tamanoextrusionObj, {
                            duration: data?.durationExtrusion ? data.durationExtrusion : 1.5,
                            tamanoextrusion: data.extrusion_depth,
                            onUpdate: () => {
                                update(tamanoextrusionObj.tamanoextrusion, theme, rendererRef.current, sceneRef.current, cameraRef.current);
                            },
                        });

                        // Animar rotación
                        gsap.to(svgGroupRef.current.rotation, {
                            duration: data?.durationRotation ?? 1.5,
                            ease: "power2.inOut",
                            x: data?.rotationX ?? oldData?.rotationX ?? 0,
                            y: data?.rotationY ?? oldData?.rotationY ?? 0,
                            z: data?.rotationZ ?? oldData?.rotationZ ?? 0,
                        });

                        // Animar posición
                        gsap.to(sceneRef.current.position, {
                            duration: data?.durationPosition ?? 1.5,
                            ease: "power2.inOut",
                            x: data?.positionX ?? oldData?.positionX ?? 0,
                            y: data?.positionY ?? oldData?.positionY ?? 0,
                            z: data?.positionZ ?? oldData?.positionZ ?? 0,
                        });

                    }
                }
            });
        };

        // Crear el observer
        const observer = new IntersectionObserver(callback, options);
        observerRef.current = { observer, currentSection: null };

        // Observar todas las secciones con IDs como "section-0", "section-1", etc.
        const sections = document.querySelectorAll('[id^="section-"]');
        sections.forEach((section) => {
            observer.observe(section);
        });
    };

    return (
        <div id="Render" className="RenderCanvasWrapper" ref={containerRef}>
            {/* Nuestro canvas de Three.js se adjuntará aquí */}
        </div>
    );
};

export default HomeBackground;
